import {
  ArcElement,
  type Chart,
  Chart as ChartJS,
  type ChartTypeRegistry,
  Legend,
  type LegendItem,
  Title,
  Tooltip,
  type TooltipOptions,
} from 'chart.js';
import { forwardRef, useId, useMemo } from 'react';
import { Pie } from 'react-chartjs-2';

import { accountingFormatNumber, getLastSevenDays, randomDataSetsTDMTotal } from '../../../../utils';

ChartJS.register(ArcElement, Title, Tooltip, Legend);

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

type PieChartProps = {
  labels: string[];
  datasets: { data: any; color: string; formattedData?: string }[];
  chartTitle?: string;
  legendPosition?: any;
  legendAlign?: any;
  showValue?: boolean;
  showValueAndPercentage?: boolean;
  currency?: boolean;
  width?: number;
  maintainAspectRatio?: boolean;
  displayLegend?: boolean;
  tooltipPlugin?: DeepPartial<TooltipOptions<keyof ChartTypeRegistry>>;
  abbreviate?: boolean;
};

const PieChart = forwardRef<Chart<'pie'> | undefined, PieChartProps>(
  (
    {
      labels,
      datasets,
      chartTitle = '',
      legendPosition,
      legendAlign,
      showValue = false,
      currency = false,
      showValueAndPercentage,
      width,
      maintainAspectRatio = false,
      displayLegend,
      tooltipPlugin,
      abbreviate,
    },
    ref
  ) => {
    const chartId = useId();
    const lastSevendaysFormatted: string[] = [];
    getLastSevenDays().forEach((date) => {
      lastSevendaysFormatted.push(date.toDateString());
    });

    const totalProject = randomDataSetsTDMTotal(datasets);
    const formattedLabels: string[] = [];
    labels.forEach((labels, i) => {
      formattedLabels.push(`${labels} (${((datasets[i].data / totalProject) * 100).toFixed(0)}%)`);
    });
    const userData: any = [];
    const dataColors: string[] = [];
    datasets.forEach((d) => {
      userData.push(d.data);
      dataColors.push(d.color);
    });

    const chartKey = useMemo(() => JSON.stringify({ labels, datasets }), [labels, datasets]);

    return (
      <Pie
        key={chartKey}
        ref={ref}
        id={chartId}
        width={width}
        data={{
          labels: labels,
          datasets: [
            {
              label: chartTitle,
              data: userData,
              backgroundColor: dataColors,
            },
          ],
        }}
        options={{
          maintainAspectRatio,
          plugins: {
            tooltip: tooltipPlugin as any,
            legend: {
              display: displayLegend,
              position: legendPosition ? legendPosition : 'left',
              align: legendAlign ? legendAlign : 'end',
              labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                font: { size: 14 },
                padding: 10,
                generateLabels: (chart: Chart<keyof ChartTypeRegistry, (number | null)[], unknown>): LegendItem[] => {
                  const { data } = chart;

                  if (data?.labels?.length && showValueAndPercentage) {
                    return data.labels.map((label, i) => ({
                      text: `${label}: ${datasets[i].formattedData}`,
                      fillStyle: dataColors[i],
                      strokeStyle: dataColors[i],
                      lineWidth: 2,
                      datasetIndex: 0,
                    }));
                  }

                  if (data?.labels?.length && showValue) {
                    return data.labels.map((label, i) => ({
                      text: `${label}: ${data?.datasets[0]?.data[i]}${showValue ? '%' : ''}`,
                      fillStyle: dataColors[i],
                      strokeStyle: dataColors[i],
                      lineWidth: 2,
                      datasetIndex: 0,
                    }));
                  }
                  if (data?.labels?.length && currency) {
                    return data.labels.map((label, i) => ({
                      text: `${label}: ${accountingFormatNumber({
                        num: data?.datasets[0]?.data[i] ?? 0,
                        format: 'en-US',
                        currency: 'USD',
                        abbreviate,
                      })}`,
                      fillStyle: dataColors[i],
                      strokeStyle: dataColors[i],
                      lineWidth: 2,
                      datasetIndex: 0,
                    }));
                  }
                  if (data?.labels?.length) {
                    return data.labels.map((label, i) => ({
                      text: `${label}: ${data?.datasets[0]?.data[i]}`,
                      fillStyle: dataColors[i],
                      strokeStyle: dataColors[i],
                      lineWidth: 2,
                      datasetIndex: 0,
                    }));
                  }
                  return [];
                },
              },
            },
          },
          scales: {
            x: { display: false },
            y: { display: false },
          },
        }}
      />
    );
  }
);

export default PieChart;
